/* eslint-disable react/no-danger */
import React, { useState, useEffect, useContext } from 'react';
import Helmet from 'react-helmet';
import FeaturesSlider from '@/components/FeaturesSlider/FeaturesSlideV2';
import { getUrlFromEnv } from '@/utils/getUrlFromEnvUtil';
import Layout from '@/components/layout';
import StickyNavigation from '@/components/StickyNavigation/StickyNavigationV2';
import Faq from '@/components/Faq/FaqV2/Faq';
import PLData from '@/data/pages/personal-loan.json';
import { redirectUrl } from '@/components/EstimateRateWidget/utils';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import {
  BREAKPOINT,
  SOFT_QUOTE_LOANS_AU_URL,
  SITE_URL,
  PL_APPLY_CTA_TRACK_ID
} from '@/utils/constants';
import { graphql, useStaticQuery } from 'gatsby';
import footerData from '@/data/json/footer.json';
import PageData from '@/data/pages/caravan-loan.json';
import styled from 'styled-components';
import Button from '../components/Button/Button';
import { PageContext } from '@/context/PageContext';
import Section from '@latitude/section-rebrand';
import ProductComparisonSection from '@/components/PersonalLoanPageContent/ProductComparisionSectionRebrand';
import PersonalLoanCalculatorV2 from '@/components/PersonalLoanCalculatorRebrandV2/PersonalLoanCalculator';
import CardCollection from '@/components/lab-components/CardCollection/CardCollectionV2';
import ProofPointCollection from '@/components/lab-components/ProofPointCollection/ProofPointCollectionV2';
import Typography from '@latitude/typography';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import PromoBanner from '@/components/lab-components/PromoBanner/PromoBannerV2';

const HomeButton = styled(Button)`
  && {
    background-color: #21a637;
    color: #fff !important;
    white-space: nowrap;
    font-size: 22px;
  }

  @media (max-width: ${BREAKPOINT.MD}) {
    && {
      font-size: 15px;
      width: 100%;
      white-space: break-spaces;
    }
  }

  margin-bottom: 8px;
  &:hover {
    box-shadow: 0 1px 13px 0 rgba(0, 0, 0, 0.4);
    transition: all 150ms ease-in-out;
  }
`;
const CTA_BUTTON_LABEL = 'Start here';
const COLOUR_PALE_BLUE = '#E5F6FF';

const CaravanLoanPage = ({ location }) => {
  const applyUrl = `${getUrlFromEnv(
    'funnel-start-loans'
  )}${SOFT_QUOTE_LOANS_AU_URL}`;

  // soft quote
  const [softQuoteHref, setSoftQuoteHref] = useState();

  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};

  useEffect(() => {
    sessionStorage.setItem('purpose', 'purpose'); // reset session val used in `EstimateRateWidgetSection`
    setSoftQuoteHref(redirectUrl('au', true));
  }, []);

  const pageQuery = useStaticQuery(graphql`
    {
      allMarkdownRemark(
        filter: {
          fileAbsolutePath: {
            glob: "**/src/templates/life-done-better/articles/*.md"
          }
          frontmatter: {
            title: { in: ["How Do Caravans and Campervans Compare?"] }
          }
        }
      ) {
        edges {
          node {
            html
            frontmatter {
              title
              path
              thumbnail
            }
          }
        }
      }
    }
  `);

  function transformData(data) {
    return {
      title: {
        nodeType: 'document',
        data: {},
        content: [
          {
            nodeType: 'heading-3',
            data: {},
            content: [
              {
                nodeType: 'text',
                value: 'Recommended reads',
                marks: [],
                data: {}
              }
            ]
          },
          {
            nodeType: 'paragraph',
            data: {},
            content: [
              {
                nodeType: 'text',
                value: '',
                marks: [],
                data: {}
              }
            ]
          }
        ]
      },
      cards: data?.map(item => ({
        image: {
          file: {
            url: `/life-done-better/images/${item?.node?.frontmatter?.thumbnail}`
          }
        },
        title: {
          data: {},
          content: [
            {
              data: {},
              content: [
                {
                  data: {},
                  marks: [],
                  value: item?.node?.frontmatter?.title,
                  nodeType: 'text'
                }
              ],
              nodeType: 'paragraph'
            }
          ],
          nodeType: 'document'
        },
        tileUrl: item?.node?.frontmatter?.path
      }))
    };
  }

  return (
    <Layout
      location={location}
      title={PageData.title}
      canonical={location.href}
      metaDesc={PageData.metaDesc}
      customFooter={footerData}
    >
      <main className="navigation-spacer" css="background-color:#f8f8f8;">
        <Helmet>
          <link rel="canonical" href={`${SITE_URL}${location.pathname}`} />
          <title>{PageData.metaTitle}</title>
          <meta name="description" content={PageData.metaDesc} />
        </Helmet>

        <HeroV2
          {...(state?.heroBannerData?.[0] ||
            PLData?.contentful?.heroBannerDataCaravan?.[0])}
        />

        <StickyNavigation
          items={PLData?.contentful?.inPageNavData.items}
          phoneNumber={PLData.content.phoneNumber}
          ctaHref={applyUrl}
          ctaText={CTA_BUTTON_LABEL}
          offsetElem="[data-sticky-navigation-offset]"
          trackEventData={{
            category: 'cta',
            action: 'quote-link',
            location: 'sticky'
          }}
          trackId="sticky-get-started"
          {...state?.inPageNavData?.[0]}
        />
        <FeaturesSlider
          key={
            (
              state?.featureSliderData?.[0] ||
              PLData?.contentful?.featureSliderData[0]
            ).heading
          }
          id="why-us"
          data={
            (
              state?.featureSliderData?.[0] ||
              PLData?.contentful?.featureSliderData[0]
            ).featureCards
          }
          className="why-choose"
          backgroundColor={
            state?.featureSliderData?.[0]?.backgroundColor?.value ||
            REBRAND_COLOR.WHITE
          }
          heading={
            (
              state?.featureSliderData?.[0] ||
              PLData?.contentful?.featureSliderData[0]
            ).heading
          }
          subheading={
            (
              state?.featureSliderData?.[0] ||
              PLData?.contentful?.featureSliderData[0]
            ).description
          }
        />
        <section
          id="repayment-calculator"
          css={`
            padding: 0px !important;
          `}
        >
          <div
            id="repayment-calculator"
            css={`
              background-color: ${REBRAND_COLOR.WHITE};
              padding-top: 32px;
              padding-bottom: 32px;

              && .typography-base__sh1 {
                margin-bottom: 30px !important;
              }
            `}
          >
            <Section>
              <Typography.SH1 color={REBRAND_COLOR.BLACK} semibold>
                Get an estimate of your repayments
              </Typography.SH1>

              <PersonalLoanCalculatorV2
                loans1
                ctaButtonLabel="Get your personalised rate"
                applyCTATrackId={PL_APPLY_CTA_TRACK_ID}
              />
            </Section>
          </div>
        </section>
        <PromoBanner
          {...(state?.promoBannerData?.[0] ||
            PLData?.contentful?.promoBannerData?.[0])}
        />
        <ProductComparisonSection
          id="compare-products"
          {...state?.productComparisonData?.[0]}
        />
        <ProofPointCollection
          id="calculators-and-tools"
          data={
            state?.proofPointCollectionData?.[0] ||
            PLData?.contentful?.proofPointCollectionData?.[0]
          }
        />
        <CardCollection
          id="applying-is-simple"
          data={
            state?.cardCollectionData?.[0] ||
            PLData?.contentful?.cardCollectionData?.[0]
          }
        />
        <CardCollection
          id="Recommended reads"
          data={
            state?.cardCollectionData?.[1] ||
            transformData(pageQuery.allMarkdownRemark.edges)
          }
          category="text-link"
        />
        {state?.faqData?.[0] && <Faq {...state?.faqData?.[0]} />}
        <ImportantInformation
          data={require('@/data/json/disclaimer/personal-loan1.json')}
          css={`
            background-color: ${REBRAND_COLOR.STONE};
            overflow-x: hidden;
            .row {
              @media (min-width: 1600px) {
                padding-left: ${PADDING.P32};
                padding-right: ${PADDING.P32};
              }
            }
          `}
          {...{
            ...state?.importantInfoData?.[0],
            sectionOneColummClasses: 'col-12 col-lg-6',
            sectionTwoColummClasses: 'col-12 col-lg-6'
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'http://schema.org',
              '@type': 'LoanOrCredit',
              name: PLData.content.loanDetails.personal.name,
              loanTerm: PLData.content.loanDetails.personal.loanTerm,
              annualPercentageRate:
                PLData.content.loanDetails.personal.annualPercentageRate,
              amount: PLData.content.loanDetails.personal.amount
            })
          }}
        />
        {/* </Box> */}
      </main>
    </Layout>
  );
};

export default CaravanLoanPage;
